import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeftIcon, 
  HeartIcon, 
  ChatBubbleLeftIcon, 
  ShareIcon,
  PaperAirplaneIcon,
  EllipsisHorizontalIcon,
  UserCircleIcon,
  PhotoIcon,
  FlagIcon,
  CodeBracketIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  BookmarkIcon,
  InformationCircleIcon,
  ArrowsPointingOutIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import { HeartIcon as HeartIconSolid, BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { 
  addPortfolioComment, 
  togglePortfolioLike,
  addCommentReply,
  addToCollection,
  setPortfolioAsPhoto,
  reportPortfolio,
  sharePortfolio,
  checkPortfolioLiked,
  fetchCommentRepliesPage,
  fetchCommentsPage,
  fetchPortfoliosPage
} from '../../store/slices/portfolioSlice';
import { deletePortfolioItem } from '../../store/slices/userSlice';
import CommentItemV2 from '../common/CommentItemV2';
import { parseCommentWithMentions } from '../../utils/mentions';
import toast from 'react-hot-toast';
import { Portfolio, PortfolioComment, Collaboration } from '../../types';
import { getPreferredImageUrl } from '../../utils/imageUtils';
import { resolveImageUrl } from '../../utils/image';
import EditPortfolioModal from './EditPortfolioModal';
import LikesListModal from './LikesListModal';
import Avatar from '../common/Avatar';
import CollaborationShowcaseModal from '../collaboration/CollaborationShowcaseModal';
import { collaborationAPI } from '../../services/api';
import PhotoCropperModal from './PhotoCropperModal';
import { uploadProfilePhoto } from '../../store/slices/userSlice';
import useLockBodyScroll from '../../hooks/useLockBodyScroll';
import useBackButton from '../../hooks/useBackButton';
import LoginPromptModal from '../common/LoginPromptModal';
import ShareModal from '../common/ShareModal';
import SmartImage from '../common/SmartImage';


interface PortfolioViewModalV2Props {
  portfolioId: string;
  onClose: () => void;
  isOwnProfile?: boolean;
  source?: 'profile' | 'feed';
  portfolios?: Portfolio[];
  onIndexChange?: (index: number) => void;
  isParentModal?: boolean;
}

const PortfolioViewModalV2: React.FC<PortfolioViewModalV2Props> = ({ 
  portfolioId, 
  onClose,
  isOwnProfile = false,
  source = 'feed',
  portfolios,
  onIndexChange,
  isParentModal = false
}) => {
  useLockBodyScroll();
  const dispatch = useAppDispatch();
  const { 
    comments, 
    commentsLoading, 
    hasMoreComments, 
    commentsPage,
    repliesPage,
    hasMoreReplies,
    repliesLoading,
    allPortfolios,
    currentPage,
    hasMorePortfolios,
    loading
  } = useAppSelector(state => state.portfolio);
  const { user } = useAppSelector(state => state.auth);
  const { portfolio: userPortfolio } = useAppSelector(state => state.user);
  
  // Determine which list to navigate
  // If portfolios prop is provided, use it
  // Otherwise, fallback to Redux state based on source
  const portfolioList = portfolios || (source === 'profile' ? userPortfolio : allPortfolios);

  // State management
  const [currentId, setCurrentId] = useState(portfolioId);
  const [commentText, setCommentText] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [replyingTo, setReplyingTo] = useState<any>(null);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showEmbedCode, setShowEmbedCode] = useState(false);

  const [isPanelOpen, setIsPanelOpen] = useState(false); 
  const [isDetailsCollapsed, setIsDetailsCollapsed] = useState(false);
  
  const portfolioItem = portfolioList.find((p: Portfolio) => p.id === currentId);
  
  const commentInputRef = useRef<HTMLInputElement>(null);
  const [allUsers, setAllUsers] = useState<any[]>([]);
  const [likeStatusLoading, setLikeStatusLoading] = useState(true);
  const [isLiked, setIsLiked] = useState(false);
  const [editingPortfolio, setEditingPortfolio] = useState<Portfolio | null>(null);
  const [selectedCollaboration, setSelectedCollaboration] = useState<Collaboration | null>(null);
  const [isLoadingCollab, setIsLoadingCollab] = useState(false);
  const [showCropper, setShowCropper] = useState(false);
  const [cropperImage, setCropperImage] = useState<string | null>(null);
  const [cropperType, setCropperType] = useState<'profile' | 'cover'>('profile');
  const [showPortfolioModal, setShowPortfolioModal] = useState(false);
  const [showLikesModal, setShowLikesModal] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [loginModalMessage, setLoginModalMessage] = useState('Sign in to interact with this content');
  const [showShareModal, setShowShareModal] = useState(false);

  // Handle Android system back button / back swipe gesture to close modal instead of navigating back
  useBackButton(
    useCallback(() => {
      if (showActionsMenu) {
        setShowActionsMenu(false);
        return true;
      }
      if (showLikesModal) {
        setShowLikesModal(false);
        return true;
      }
      if (showShareModal) {
        setShowShareModal(false);
        return true;
      }
      if (showLoginModal) {
        setShowLoginModal(false);
        return true;
      }
      if (isPanelOpen) {
        setIsPanelOpen(false);
        return true;
      }
      onClose();
      return true;
    }, [showActionsMenu, showLikesModal, showShareModal, showLoginModal, isPanelOpen, onClose]),
    true,
    100
  );

  // Screen size detection for responsive animations
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Navigation Logic - use useMemo to ensure reactivity
  const currentIndex = useMemo(() => 
    portfolioList.findIndex((p: Portfolio) => p.id === currentId),
    [portfolioList, currentId]
  );
  
  const hasNext = useMemo(() => 
    currentIndex < portfolioList.length - 1,
    [currentIndex, portfolioList.length]
  );
  
  const hasPrev = useMemo(() => 
    currentIndex > 0,
    [currentIndex]
  );

  // Debug: Track portfolioList changes
  // useEffect(() => {
  //   console.log('📊 Portfolio List Updated:', {
  //     source,
  //     listLength: portfolioList.length,
  //     currentId,
  //     currentIndex,
  //     hasNext,
  //     hasPrev,
  //     firstItem: portfolioList[0]?.id,
  //     lastItem: portfolioList[portfolioList.length - 1]?.id
  //   });
  // }, [portfolioList.length, source]);

  // Close panel and reset state when navigating to a collaboration
  useEffect(() => {
    if (portfolioItem?.is_collaboration) {
      setIsPanelOpen(false);
      setIsLiked(false);
    }
  }, [portfolioItem?.is_collaboration, currentId]);

  // Infinite Scroll Trigger
  useEffect(() => {
    // Debug logging
    // console.log('Infinite Scroll Debug:', {
    //   source,
    //   hasMorePortfolios,
    //   loading,
    //   currentIndex,
    //   listLength: portfolioList.length,
    //   threshold: portfolioList.length - 2
    // });

    if (source === 'feed' && hasMorePortfolios && !loading && currentIndex >= portfolioList.length - 2) {
      // console.log('Triggering fetchPortfoliosPage...');
      dispatch(fetchPortfoliosPage({ page: currentPage + 1 }));
    }
  }, [currentIndex, source, hasMorePortfolios, loading, currentPage, dispatch, portfolioList.length]);

  // Sync index change whenever currentId changes
  useEffect(() => {
    if (onIndexChange && currentIndex !== -1) {
      onIndexChange(currentIndex);
    }
  }, [currentIndex, onIndexChange]);

  const handleNext = useCallback(async () => {
    if (hasNext) {
      // Navigate to existing next item
      const nextId = portfolioList[currentIndex + 1].id;
      setCurrentId(nextId);
    } else if (source === 'feed' && hasMorePortfolios && !loading) {
      // At the end, fetch more and navigate to first new item
      // console.log('🔄 Fetching next page before navigation...');
      const result: any = await dispatch(fetchPortfoliosPage({ page: currentPage + 1 }));
      
      // After fetch completes, navigate to the first item of the new page
      if (result.payload?.portfolios && result.payload.portfolios.length > 0) {
        const nextId = result.payload.portfolios[0].id;
        setCurrentId(nextId);
      }
    }
  }, [hasNext, portfolioList, currentIndex, source, hasMorePortfolios, loading, currentPage, dispatch]);

  const handlePrev = useCallback(() => {
    if (hasPrev) {
      const prevId = portfolioList[currentIndex - 1].id;
      setCurrentId(prevId);
    }
  }, [hasPrev, portfolioList, currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') handlePrev();
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleNext, handlePrev, onClose]);

  // Extract all users from comments
  useEffect(() => {
    if (comments.length > 0) {
      const users: any[] = [];
      comments.forEach(comment => {
        if (comment.user) users.push(comment.user);
        if (comment.replies) {
          comment.replies.forEach((reply: any) => {
            if (reply.user) users.push(reply.user);
          });
        }
      });
      const uniqueUsers = users.filter((user, index, self) => 
        index === self.findIndex(u => u.id === user.id)
      );
      setAllUsers(uniqueUsers);
    }
  }, [comments]);

  const userId = user == null ? "" : user.id;

  // Initial load & ID change
  useEffect(() => {
    if (!currentId || portfolioItem?.is_collaboration) return;
    dispatch(fetchCommentsPage({ portfolioId: currentId, page: 1, userId }));
  }, [currentId, dispatch, userId, portfolioItem?.is_collaboration]);

  // Load more comments
  const handleLoadMoreComments = () => {
    if (hasMoreComments && !commentsLoading) {
      dispatch(fetchCommentsPage({ 
        portfolioId: currentId, 
        page: commentsPage + 1,
        userId: userId 
      }));
    }
  };

  const handlePostPortfolioEdit = () => {
    setEditingPortfolio(null);
  }

  const handleDeletePortfolio = async (id: string) => {
      if (window.confirm('Are you sure you want to delete this portfolio item?')) {
        await dispatch(deletePortfolioItem(id));
        onClose();
      }
    };

  // Load more replies
  const handleLoadMoreReplies = (commentId: string) => {
    const currentPage = repliesPage[commentId] || 0;
    dispatch(fetchCommentRepliesPage({ 
      commentId, 
      page: currentPage + 1,
      userId: userId
    }));
  };

  useEffect(() => {
      const checkLikeStatus = async () => {
        if (!user?.id || !currentId || portfolioItem?.is_collaboration) {
          setLikeStatusLoading(false);
          return;
        }
        try {
          const result = await dispatch(checkPortfolioLiked(currentId));
          if (checkPortfolioLiked.fulfilled.match(result)) {
            setIsLiked(result.payload.is_liked);
          }
        } catch (error) {
          console.error('Failed to check like status:', error);
        } finally {
          setLikeStatusLoading(false);
        }
      };
      checkLikeStatus();
    }, [dispatch, currentId, user?.id]);

  // Handle starting a reply
  const handleReplyClick = (comment: PortfolioComment) => {
    const mentionText = `@${comment.user.first_name} ${comment.user.last_name} `;
    if (comment.is_reply) {
        setReplyingTo(comment.parent_comment_id)
    } else {
        setReplyingTo(comment.id);
    }
    setCommentText(mentionText);
    setIsPanelOpen(true); // Open panel to reply
    setTimeout(() => {
      if (commentInputRef.current) {
        commentInputRef.current.focus();
        const position = mentionText.length;
        commentInputRef.current.setSelectionRange(position, position);
      }
    }, 100);
  };

  // Handle form submission
  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!commentText.trim()) return;

    setSubmittingComment(true);
    try {
      if (replyingTo) {
        await dispatch(addCommentReply({
          commentId: replyingTo,
          content: commentText.trim()
        })).unwrap();
      } else {
        if (!currentId) return;
        await dispatch(addPortfolioComment({
          portfolioId: currentId,
          content: commentText.trim()
        })).unwrap();
      }
      setCommentText('');
      setReplyingTo(null);
    } catch (error) {
      console.error('Failed to submit comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Handle portfolio like
  const handlePortfolioLike = async () => {
    if (!user) {
      setLoginModalMessage('Sign in to like this portfolio item');
      setShowLoginModal(true);
      return;
    }
    if (!currentId) return;
    try {
      await dispatch(togglePortfolioLike(currentId)).unwrap();
      setIsLiked(!isLiked)
    } catch (error) {
      toast.error('Failed to update like');
    }
  };

  const handleShare = async () => {
    if (!portfolioItem?.id) return;
    setShowShareModal(true);
  };

  const handleViewCollaboration = async () => {
    if (isParentModal) {
      onClose();
      return;
    }
    if (!portfolioItem?.collaboration_id) return;
    setIsLoadingCollab(true);
    try {
      const response = await collaborationAPI.getCollaboration(portfolioItem.collaboration_id);
      if (response.data.success) {
        setSelectedCollaboration(response.data.data);
      }
    } catch (error) {
      console.error('Failed to fetch collaboration details', error);
      toast.error('Failed to load collaboration details');
    } finally {
      setIsLoadingCollab(false);
    }
  };

  // Handle actions
  // Handle actions
  const handleSetAsProfilePhoto = () => {
    if (!imageUrl) return;
    setCropperImage(imageUrl);
    setCropperType('profile');
    setShowCropper(true);
    setShowActionsMenu(false);
  };

  const handleSetAsCoverPhoto = () => {
    if (!imageUrl) return;
    setCropperImage(imageUrl);
    setCropperType('cover');
    setShowCropper(true);
    setShowActionsMenu(false);
  };

  const handleCropComplete = async (croppedImage: Blob) => {
    const formData = new FormData();
    formData.append('image', croppedImage, 'image.jpg');

    try {
      await dispatch(uploadProfilePhoto({ type: cropperType, file: formData })).unwrap();
      window.location.reload();
      setShowCropper(false);
    } catch (error) {
      toast.error(`Failed to update ${cropperType} photo`);
    }
  };

  const handleAddToCollection = async () => {
    if (!currentId) return;
    try {
      await dispatch(addToCollection(currentId)).unwrap();
      toast.success('Added to collection!');
    } catch (error) {
      toast.error('Failed to add to collection');
    }
    setShowActionsMenu(false);
  };

  const handleReport = async () => {
    if (!currentId) return;
    const reason = prompt('Please provide a reason for reporting this content:');
    if (reason) {
      try {
        await dispatch(reportPortfolio({ portfolioId: currentId, reason })).unwrap();
        toast.success('Content reported successfully');
      } catch (error) {
        toast.error('Failed to report content');
      }
    }
    setShowActionsMenu(false);
  };

  const handleShowEmbedCode = () => {
    setShowEmbedCode(true);
    setShowActionsMenu(false);
  };

  const generateEmbedCode = () => {
    return `<iframe src="${window.location.origin}/embed/portfolio/${currentId}" width="400" height="600" frameborder="0"></iframe>`;
  };

  const copyEmbedCode = () => {
    navigator.clipboard.writeText(generateEmbedCode());
    toast.success('Embed code copied to clipboard!');
  };

  const formatTimeAgo = (date: string) => {
    const now = new Date();
    const portfolioDate = new Date(date);
    const diffInSeconds = Math.floor((now.getTime() - portfolioDate.getTime()) / 1000);
    
    if (diffInSeconds < 60) return 'just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d`;
    return `${Math.floor(diffInSeconds / 604800)}w`;
  };

  const imageUrl = portfolioItem ? getPreferredImageUrl(
    portfolioItem.image_url_signed || portfolioItem.image_url,
    portfolioItem.medium_image_url_signed || portfolioItem.medium_image_url,
    user?.image_quality_preference
  ) : '';

  if (!portfolioItem) return null;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 z-[200] flex items-center justify-center overflow-hidden">
      {/* Dynamic Blurred Background */}
      <div className="absolute inset-0 z-0">
        <div 
          className="absolute inset-0 bg-cover bg-center blur-3xl opacity-40 scale-110 transition-all duration-700 ease-in-out"
          style={{ backgroundImage: `url("${imageUrl}")` }}
        />
        <div className="absolute inset-0 bg-black/90" />
      </div>

      {/* Close Button (Top Right) */}
      <button 
        onClick={onClose}
        className="absolute right-4 md:right-6 z-50 p-2 md:p-3 bg-black/40 backdrop-blur-md rounded-full text-white/80 hover:text-white hover:bg-white/20 transition-all border border-white/20 shadow-lg"
        style={{ top: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 1rem)' }}
      >
        <XMarkIcon className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Navigation Arrows - Hidden on mobile, visible on md+ */}
      {hasPrev && (
        <button
          onClick={handlePrev}
          className="hidden md:block absolute left-4 top-1/2 transform -translate-y-1/2 z-50 p-3 bg-black/20 backdrop-blur-md rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all border border-white/10 group"
        >
          <ChevronLeftIcon className="w-8 h-8 group-hover:-translate-x-1 transition-transform" />
        </button>
      )}
      
      
      {(hasNext || (source === 'feed' && hasMorePortfolios && !loading)) && (
        <button
          onClick={handleNext}
          className="hidden md:block absolute right-4 top-1/2 transform -translate-y-1/2 z-50 p-3 bg-black/20 backdrop-blur-md rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-all border border-white/10 group"
        >
          <ChevronRightIcon className="w-8 h-8 group-hover:translate-x-1 transition-transform" />
        </button>
      )}

      {/* Main Content Area */}
      <div className="relative z-10 w-full h-full flex flex-col md:flex-row">
        
        {/* Image Stage (Left/Center) */}
        <motion.div 
          className="flex-1 relative flex items-center justify-center p-0 md:p-12 h-full"
          animate={!isMobile ? { 
            width: isPanelOpen ? 'calc(100% - 400px)' : '100%',
            paddingRight: isPanelOpen ? '2rem' : '3rem',
            paddingLeft: '3rem'
          } : {}}
          transition={{ type: "spring", stiffness: 300, damping: 30 }}
        >
          {/* Floating User Pill (Top Left) */}
          <motion.div 
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute left-4 md:left-12 flex items-center gap-3 bg-black/50 backdrop-blur-md px-3 py-1.5 md:px-4 md:py-2 rounded-full border border-white/20 hover:bg-black/70 transition-colors cursor-pointer z-40 shadow-lg"
            style={{ top: 'calc(max(var(--safe-area-inset-top, 0px), env(safe-area-inset-top, 0px)) + 1rem)' }}
            onClick={() => window.open(`/profile/${portfolioItem.user?.id}`, '_blank')}
          >
            <Avatar 
              src={portfolioItem.user?.profile_pic_thumb_signed || portfolioItem.user?.profile_pic} 
              alt={portfolioItem.user?.first_name || 'User'}
              size="sm"
              className="w-6 h-6 md:w-8 md:h-8 ring-2 ring-white/20"
            />
            <div className="flex flex-col">
              <span className="text-xs md:text-sm font-bold text-white">
                {portfolioItem.user?.first_name} {portfolioItem.user?.last_name}
              </span>
              <span className="text-[10px] text-white/60 hidden md:block">
                {portfolioItem.user?.location || 'Portfolio Item'}
              </span>
            </div>
          </motion.div>

          {/* Main Image */}
          <motion.div
            key={portfolioItem.id}
            className="relative flex items-center justify-center h-full w-full max-w-full mx-auto"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            drag="x"
            dragConstraints={{ left: 0, right: 0 }}
            dragElastic={1}
            onDragEnd={(e, { offset, velocity }) => {
              const swipe = offset.x;

              if (swipe < -50 || velocity.x < -500) {
                handleNext();
              } else if (swipe > 50 || velocity.x > 500) {
                handlePrev();
              }
            }}
          >
            <SmartImage
                src={imageUrl}
                fallbackSrc={resolveImageUrl(portfolioItem.thumbnail_image_url)}
                alt={portfolioItem.title}
                showOfflinePlaceholder={false}
                className="max-w-full max-h-[80vh] md:max-h-full object-contain shadow-2xl rounded-lg pointer-events-none block transition-transform duration-300"
                style={{
                  transform: portfolioItem.rotation ? `rotate(${portfolioItem.rotation}deg)` : undefined,
                }}
            />
          </motion.div>

          {/* Floating Info Panel (Bottom Left) - Always Visible */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={`absolute left-0 right-0 md:left-12 md:right-auto md:max-w-md z-30 transition-all duration-300 ease-in-out ${
                isDetailsCollapsed 
                    ? '' 
                    : 'p-6 bg-gradient-to-t from-black/90 via-black/50 to-transparent md:bg-black/30 md:backdrop-blur-md md:rounded-2xl md:border md:border-white/10'
            }`}
            style={{
              bottom: isDetailsCollapsed 
                ? 'calc(max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)) + 5rem)' 
                : 'calc(max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)) + 5rem)'
            }}
          >
             {/* Collapse Toggle (Desktop only or both? Let's do both for consistency, but style appropriately) */}
             <div className="flex items-center justify-between mb-2">
                {!isDetailsCollapsed && (
                    <h1 className="text-lg md:text-2xl font-bold drop-shadow-md text-white mr-2 line-clamp-1">{portfolioItem.title}</h1>
                )}
                
                <button 
                    onClick={(e) => {
                        e.stopPropagation();
                        setIsDetailsCollapsed(!isDetailsCollapsed);
                    }}
                    className={`p-1.5 rounded-full hover:bg-white/10 transition-colors text-white/80 hover:text-white outline-none focus:outline-none ${isDetailsCollapsed ? 'bg-black/40 backdrop-blur-md border border-white/10' : ''}`}
                    title={isDetailsCollapsed ? "Show Details" : "Hide Details"}
                >
                    {isDetailsCollapsed ? (
                        <div className="flex items-center gap-2 px-2">
                            <InformationCircleIcon className="w-5 h-5" />
                            <span className="text-xs font-medium">Show Details</span>
                        </div>
                    ) : (
                        <ChevronDownIcon className="w-5 h-5" />
                    )}
                </button>
             </div>

             <AnimatePresence>
                {!isDetailsCollapsed && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.2 }}
                    >
                        <div className="text-xs md:text-sm text-white/90 leading-relaxed whitespace-pre-wrap max-h-20 md:max-h-32 overflow-y-auto custom-scrollbar drop-shadow-md">
                            {parseCommentWithMentions(portfolioItem.description || '', () => {})}
                        </div>
                        {portfolioItem.tags && (
                            <div className="flex flex-wrap gap-2 mt-2 md:mt-3">
                            {portfolioItem.tags.map((tag, i) => (
                                <span key={i} className="px-2 py-1 bg-white/20 backdrop-blur-sm text-white text-[10px] md:text-xs rounded-md font-medium">#{tag}</span>
                            ))}
                            </div>
                        )}
                        <div className="flex items-center gap-3 md:gap-4 mt-3 md:mt-4 text-[10px] md:text-xs text-white/70 font-medium uppercase tracking-wider drop-shadow-md">
                            <span>{formatTimeAgo(portfolioItem.created_at)}</span>
                            <span>•</span>
                            <span 
                              className="cursor-pointer hover:underline hover:text-white transition-colors"
                              onClick={(e) => {
                                e.stopPropagation();
                                setShowLikesModal(true);
                              }}
                            >
                              {portfolioItem.likes || 0} Likes
                            </span>
                            <span>•</span>
                            <span>{portfolioItem.comments_count || 0} Comments</span>
                        </div>
                    </motion.div>
                )}
             </AnimatePresence>
          </motion.div>

          {/* Floating Action Bar (Bottom Center) */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-1 md:gap-2 bg-black/60 backdrop-blur-xl px-3 py-2 md:px-6 md:py-3 rounded-full border border-white/10 shadow-xl z-40 max-w-[95vw]"
            style={{
              bottom: 'calc(max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)) + 1.25rem)'
            }}
          >
            {!portfolioItem.is_collaboration && (
              <>
                <button 
                  onClick={handlePortfolioLike}
                  className="p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors group outline-none focus:outline-none flex-shrink-0"
                >
                  {isLiked ? (
                    <HeartIconSolid className="w-5 h-5 md:w-6 md:h-6 text-red-500" />
                  ) : (
                    <HeartIcon className="w-5 h-5 md:w-6 md:h-6 text-white group-hover:text-red-400 transition-colors" />
                  )}
                </button>
                <div className="w-px h-4 md:h-6 bg-white/20 mx-0.5 md:mx-1 flex-shrink-0" />
                <button 
                  onClick={() => {
                    if (!user) {
                      setLoginModalMessage('Sign in to view and post comments');
                      setShowLoginModal(true);
                      return;
                    }
                    setIsPanelOpen(!isPanelOpen);
                  }}
                  className={`p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors outline-none focus:outline-none flex-shrink-0 ${isPanelOpen ? 'text-blue-400' : 'text-white'}`}
                >
                  <ChatBubbleLeftIcon className="w-5 h-5 md:w-6 md:h-6" />
                </button>
                <div className="w-px h-4 md:h-6 bg-white/20 mx-0.5 md:mx-1 flex-shrink-0" />
              </>
            )}

            {portfolioItem.is_collaboration && (
               <>
               <button 
                  onClick={handleViewCollaboration}
                  disabled={isLoadingCollab}
                  className={`p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors text-white outline-none focus:outline-none flex-shrink-0 ${isLoadingCollab ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title="View Collaboration"
               >
                  {isLoadingCollab ? (
                    <div className="w-5 h-5 md:w-6 md:h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <ArrowsPointingOutIcon className="w-5 h-5 md:w-6 md:h-6" />
                  )}
               </button>
               <div className="w-px h-4 md:h-6 bg-white/20 mx-0.5 md:mx-1 flex-shrink-0" />
               </>
            )}
            <button 
              onClick={() => {
                if (!user) {
                  setLoginModalMessage('Sign in to share this portfolio item');
                  setShowLoginModal(true);
                  return;
                }
                handleShare();
              }}
              className="p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors text-white outline-none focus:outline-none flex-shrink-0"
            >
              <PaperAirplaneIcon className="w-5 h-5 md:w-6 md:h-6 -rotate-45 mb-1" />
            </button>
            <div className="w-px h-4 md:h-6 bg-white/20 mx-0.5 md:mx-1 flex-shrink-0" />
            <div className="relative flex items-center flex-shrink-0">
              <button 
                onClick={() => {
                  if (!user) {
                    setLoginModalMessage('Sign in to access more options');
                    setShowLoginModal(true);
                    return;
                  }
                  setShowActionsMenu(!showActionsMenu);
                }}
                className="p-1.5 md:p-2 rounded-full hover:bg-white/10 transition-colors text-white outline-none focus:outline-none flex items-center justify-center"
              >
                <EllipsisHorizontalIcon className="w-5 h-5 md:w-6 md:h-6" />
              </button>
              {/* Actions Dropdown (Floating) */}
              <AnimatePresence>
                {showActionsMenu && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    className={`absolute bottom-full mb-4 w-48 bg-gray-900/90 backdrop-blur-xl rounded-xl border border-white/10 shadow-2xl overflow-hidden py-1 ${
                      isMobile ? 'right-0 origin-bottom-right' : 'left-1/2 transform -translate-x-1/2 origin-bottom'
                    }`}
                  >
                    {isOwnProfile && (
                      <button
                        onClick={() => { setEditingPortfolio(portfolioItem); setShowActionsMenu(false); }}
                        className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/10 flex items-center gap-3"
                      >
                        <PencilIcon className="w-4 h-4" /> Edit
                      </button>
                    )}
                    {/* <button onClick={handleSetAsProfilePhoto} className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/10 flex items-center gap-3">
                      <UserCircleIcon className="w-4 h-4" /> Set as Profile
                    </button>
                    <button onClick={handleSetAsCoverPhoto} className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/10 flex items-center gap-3">
                      <PhotoIcon className="w-4 h-4" /> Set as Cover
                    </button> */}
                    <button onClick={handleShowEmbedCode} className="w-full px-4 py-3 text-left text-sm text-white hover:bg-white/10 flex items-center gap-3">
                      <CodeBracketIcon className="w-4 h-4" /> Embed
                    </button>
                    <button onClick={handleReport} className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3">
                      <FlagIcon className="w-4 h-4" /> Report
                    </button>
                    {isOwnProfile && (
                      <button onClick={() => handleDeletePortfolio(portfolioItem.id)} className="w-full px-4 py-3 text-left text-sm text-red-400 hover:bg-red-500/10 flex items-center gap-3">
                        <TrashIcon className="w-4 h-4" /> Delete
                      </button>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </motion.div>

        {/* Collapsible Side Panel (Right) - Comments Only */}
        <AnimatePresence>
          {isPanelOpen && (
            <motion.div 
              initial={isMobile ? { y: '100%', opacity: 0 } : { x: '100%', opacity: 0 }}
              animate={isMobile ? { y: 0, opacity: 1 } : { x: 0, opacity: 1 }}
              exit={isMobile ? { y: '100%', opacity: 0 } : { x: '100%', opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className={`
                bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl shadow-2xl absolute z-50 flex flex-col
                ${isMobile 
                  ? 'bottom-0 left-0 right-0 h-[75vh] rounded-t-3xl border-t border-gray-200 dark:border-gray-800' 
                  : 'right-0 top-0 w-[400px] h-full border-l border-gray-200 dark:border-gray-800'
                }
              `}
              style={isMobile ? {
                paddingBottom: 'calc(max(var(--safe-area-inset-bottom, 0px), env(safe-area-inset-bottom, 0px)) + 1rem)'
              } : {}}
            >
              {/* Mobile Drag Handle */}
              {isMobile && (
                <div className="w-full flex justify-center pt-3 pb-1" onClick={() => setIsPanelOpen(false)}>
                  <div className="w-12 h-1.5 bg-gray-300 dark:bg-gray-700 rounded-full" />
                </div>
              )}
              {/* Panel Header */}
              <div className="p-6 border-b border-gray-100 dark:border-gray-800 flex items-center justify-between bg-white/50 dark:bg-gray-900/50">
                <h3 className="font-bold text-gray-900 dark:text-white">Comments ({portfolioItem.comments_count || comments.length})</h3>
                <button onClick={() => setIsPanelOpen(false)} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400">
                  <ArrowsPointingOutIcon className="w-5 h-5" />
                </button>
              </div>

              {/* Scrollable Content */}
              <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                {/* Comments Section */}
                <div className="space-y-6">
                  {commentsLoading && comments.length === 0 ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
                    </div>
                  ) : comments.length === 0 ? (
                    <div className="text-center py-12 text-gray-400 dark:text-gray-500 text-sm bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-dashed border-gray-200 dark:border-gray-700">
                      No comments yet. Be the first to share your thoughts!
                    </div>
                  ) : (
                    <>
                      {comments.map((comment) => (
                        comment && (
                          <CommentItemV2
                            key={comment.id}
                            comment={comment}
                            commentId={comment.id}
                            portfolioId={comment.portfolio_id}
                            onReply={handleReplyClick}
                            onLoadMoreReplies={handleLoadMoreReplies}
                            allUsers={allUsers}
                            hasMoreReplies={hasMoreReplies[comment.id]}
                            repliesLoading={repliesLoading[comment.id]}
                            portfolioOwnerId={portfolioItem.user?.id || ''}
                          />
                        )
                      ))}
                      {hasMoreComments && (
                        <button 
                          onClick={handleLoadMoreComments}
                          className="w-full py-3 text-xs text-gray-500 dark:text-gray-400 font-medium hover:text-gray-900 dark:hover:text-white hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg transition-colors"
                        >
                          Load more comments
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Comment Input (Sticky Bottom) */}
              <div className="p-4 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-800">
                <form onSubmit={handleCommentSubmit} className="relative">
                  <input
                    ref={commentInputRef}
                    type="text"
                    value={commentText}
                    onChange={(e) => setCommentText(e.target.value)}
                    placeholder="Add a comment..."
                    className="w-full bg-gray-50 dark:bg-gray-800 border-none rounded-xl py-3 pl-4 pr-12 text-sm focus:ring-2 focus:ring-blue-500/20 dark:focus:ring-blue-400/20 placeholder-gray-400 dark:placeholder-gray-500 text-gray-900 dark:text-white"
                    disabled={submittingComment}
                  />
                  <button 
                    type="submit"
                    disabled={!commentText.trim() || submittingComment}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-2 text-blue-600 hover:bg-blue-50 rounded-lg disabled:opacity-50 disabled:hover:bg-transparent transition-colors"
                  >
                    {submittingComment ? (
                      <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <PaperAirplaneIcon className="w-5 h-5" />
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Embed Code Modal */}
      {showEmbedCode && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold mb-4">Embed this post</h3>
            <div className="bg-gray-100 p-3 rounded-lg text-xs font-mono break-all mb-4">
              {generateEmbedCode()}
            </div>
            <div className="flex gap-3">
              <button onClick={copyEmbedCode} className="flex-1 bg-blue-600 text-white py-2 rounded-lg font-medium hover:bg-blue-700">
                Copy Code
              </button>
              <button onClick={() => setShowEmbedCode(false)} className="flex-1 border border-gray-300 py-2 rounded-lg font-medium hover:bg-gray-50">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Modal */}
      {editingPortfolio && (
        <EditPortfolioModal
          portfolio={editingPortfolio}
          onClose={() => setEditingPortfolio(null)}
          onSuccess={handlePostPortfolioEdit}
        />
      )}
      {selectedCollaboration && (
        <CollaborationShowcaseModal
          onClose={() => setSelectedCollaboration(null)}
          collaboration={selectedCollaboration}
          initialImageIndex={(() => {
            if (portfolioItem?.is_collaboration && portfolioItem.id.includes('_')) {
              const parts = portfolioItem.id.split('_');
              const idx = parseInt(parts[parts.length - 1]);
              return !isNaN(idx) ? Math.max(0, idx - 1) : 0;
            }
            return 0;
          })()}
        />
      )}

      {showCropper && cropperImage && (
        <PhotoCropperModal
          type={cropperType}
          photoToCrop={cropperImage}
          onClose={() => setShowCropper(false)}
          onCropConfirm={handleCropComplete}
        />
      )}

      {/* Login Prompt Modal */}
      <LoginPromptModal
        isOpen={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        message={loginModalMessage}
      />

      {/* Share Modal */}
      {portfolioItem && (
        <ShareModal
            isOpen={showShareModal}
            onClose={() => setShowShareModal(false)}
            title={portfolioItem.title}
            url={`${window.location.origin}/portfolio/${portfolioItem.id}`}
            type="portfolio"
            data={{
                id: portfolioItem.id,
                title: portfolioItem.title,
                image: imageUrl,
                user_id: portfolioItem.user_id
            }}
        />
      )}

      <LikesListModal
        isOpen={showLikesModal}
        onClose={() => setShowLikesModal(false)}
        portfolioId={portfolioItem?.id || ''}
      />
    </div>,
    document.body
  );
};

export default PortfolioViewModalV2;